<template>
  <div>
    <Subheader title="How to Receive your Funds" />

    <div class="app-content-column">
      <form class="card" @submit.stop.prevent="submitBankingInfo">
        <h3>Finalizing Your Profile</h3>
        <p>
          To receive funds, we'll need your mPesa information. Currently, our payout workflow is set
          up to send funds to mPesa accounts only. We're working hard to add other payout methods
          soon! If you need to have another payout method set up, please email us at
          <a href="mailto:mzingopartners@gmail.com">mzingopartners@gmail.com</a> and we will see if
          we can help!
        </p>

        <h3>Your mPesa Details</h3>
        <p>
          By providing your mPESA information, you are authorizing Mzingo Partners to send funds to
          your mPesa account for the use on your Mzingo Partners related activities. Your funds will
          be on a 10 day delay to ensure our international transfers can move to mPESA friendly
          accounts. We will be periodically monitoring your Mzingo Partners account to ensure your
          projects fall within our guidelines.
        </p>

        <div class="form">
          <Field :errors="fieldErrors" :show-field-errors="showFieldErrors" name="bankingFirstName">
            <label for="payout-setup-banking-first-name" class="label">First Name</label>
            <input
              id="payout-setup-banking-first-name"
              v-model="userData.bankingFirstName"
              type="text"
              required
            />
          </Field>

          <Field :errors="fieldErrors" :show-field-errors="showFieldErrors" name="bankingLastName">
            <label for="payout-setup-banking-last-name" class="label">Last Name</label>
            <input
              id="payout-setup-banking-last-name"
              v-model="userData.bankingLastName"
              type="text"
              required
            />
          </Field>

          <Field
            :errors="fieldErrors"
            :show-field-errors="showFieldErrors"
            name="bankingPhoneNumber"
          >
            <label for="payout-setup-banking-phone-number" class="label">Phone Number</label>
            <vue-tel-input
              id="payout-setup-banking-phone-number"
              v-model="userData.bankingPhoneNumber"
              wrapper-classes="tel-input"
              placeholder="Mobile Phone"
              mode="international"
              :max-len="20"
              :valid-characters-only="true"
              :required="true"
            ></vue-tel-input>
          </Field>
        </div>

        <div class="button-wrapper is-centered">
          <button type="submit" class="medium-button">
            Submit Banking Information
          </button>
        </div>

        <router-link :to="{ name: 'Home' }">
          <h5>Skip for Now</h5>
        </router-link>
      </form>
    </div>
  </div>
</template>

<script>
import { VueTelInput } from 'vue-tel-input'

import Subheader from '@/components/Subheader'
import Field from '@/components/Field'
import User from '@/services/users'
import { mapGetters } from 'vuex'
import { validateInputLength, validatePhoneInput } from '@/services/forms'

export default {
  name: 'PayoutSetup',
  components: {
    VueTelInput,
    Subheader,
    Field,
  },
  data() {
    return {
      userData: {
        bankingFirstName: null,
        bankingLastName: null,
        bankingPhoneNumber: null,
      },
      loading: false,
      fieldErrors: [],
      showFieldErrors: false,
      user: {},
      fromNewProjectGoalsPage: false,
    }
  },
  computed: {
    ...mapGetters(['getUser']),
  },
  created() {
    if (this.$route.params.fromProjectGoalsPage) {
      this.fromNewProjectGoalsPage = true
    }

    this.user = this.getUser
    // Get the banking phone number, banking first name and banking last name in the user model. If it doesn't exist, use the user's phone number as a default.
    if (this.user.bankingPhoneNumber) {
      this.userData.bankingPhoneNumber = this.user.bankingPhoneNumber
    } else {
      this.userData.bankingPhoneNumber = this.user.phoneNumber
    }

    if (this.user.bankingFirstName) {
      this.userData.bankingFirstName = this.user.bankingFirstName
    } else {
      this.userData.bankingFirstName = this.user.firstName
    }

    if (this.user.bankingLastName) {
      this.userData.bankingLastName = this.user.bankingLastName
    } else {
      this.userData.bankingLastName = this.user.lastName
    }
  },
  methods: {
    submitBankingInfo() {
      const fields = ['bankingFirstName', 'bankingLastName', 'bankingPhoneNumber']
      this.user.bankingFirstName = this.userData.bankingFirstName
      this.user.bankingLastName = this.userData.bankingLastName
      this.user.bankingPhoneNumber = this.userData.bankingPhoneNumber
      this.fieldErrors = this.collectFieldErrors()

      if (this.fieldErrors.length) {
        this.showFieldErrors = true
      } else {
        User.api
          .update(this.user, fields)
          .then(response => {
            if (this.fromNewProjectGoalsPage) {
              // New Makers workflow: directs to newly created Project
              this.successUpdateUser(response)
              this.$router.push({
                name: 'Project',
                params: { projectId: this.$route.params.projectId },
              })
            } else {
              // Otherwise (from SideNavigation): just update user and alert success
              this.successUpdateUser(response)
            }
          })
          .catch(() => {
            this.$Alert.alert({
              type: 'error',
              message: `<h2>Payment Details Not Saved. Contact us for help if this continues to happen.</h2>`,
              timeout: 3000,
            })
          })
      }
    },
    collectFieldErrors() {
      const errors = []

      Object.keys(this.userData).forEach(field => {
        const error = validateInputLength(field, this.userData[field])
        if (error) {
          errors.push(error)
        }

        if (field === 'bankingPhoneNumber') {
          const error = validatePhoneInput(field, this.userData[field])
          if (error) {
            errors.push(error)
          }
        }
      })

      return errors
    },
    successUpdateUser(response) {
      this.$store.commit('UPDATE_USER', response)
      this.$Alert.alert({
        type: 'success',
        message: `<h2>Payment Information updated</h2>`,
        timeout: 3000,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';
@import '../styles/mixins/cards';
@import '../styles/mixins/buttons';
@import '../styles/forms';
@import '../styles/layout';

.card {
  @include card-w-borders();
  align-items: center;
  margin-top: 1.25rem;
}
</style>
